import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { TRANSLATIONS, LANGS, FALLBACK_LANG } from './translations';

// ── Architecture de la langue (source de vérité unique) ──────────
// Résolution, par priorité :
//   1. Choix EXPLICITE de l'utilisateur (localStorage 'oba_lang', posé par le
//      bouton 🌐 ou le sélecteur d'inscription, et synchronisé sur son profil
//      serveur users.language quand il est connecté) ;
//   2. Langue du NAVIGATEUR (navigator.language — reflète la locale réelle de
//      la personne, plus fiable et plus respectueux qu'une géoloc par IP) ;
//   3. Français (marché principal).
// L'arabe bascule automatiquement le document en dir="rtl".

const LANG_KEY = 'oba_lang';
const SUPPORTED = LANGS.map((l) => l.code);

export const detectLang = () => {
  const saved = localStorage.getItem(LANG_KEY);
  if (SUPPORTED.includes(saved)) return saved;
  const nav = String(navigator.language || navigator.userLanguage || '').toLowerCase();
  if (nav.startsWith('ar')) return 'ar';
  if (nav.startsWith('en')) return 'en';
  return 'fr';
};

// Lit une clé dans un dictionnaire : d'abord à plat (clés sources contenant
// espaces et accents), puis en descendant le chemin pointé. Renvoie undefined
// si la clé est absente ou ne pointe pas sur une chaîne — ce qui laisse le
// repli de t() faire son travail.
const resolve = (dict, key) => {
  if (!dict || typeof key !== 'string') return undefined;
  const flat = dict[key];
  if (typeof flat === 'string') return flat;
  let node = dict;
  for (const part of key.split('.')) {
    if (node === null || typeof node !== 'object') return undefined;
    node = node[part];
  }
  return typeof node === 'string' ? node : undefined;
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectLang);

  // Direction du document : l'arabe passe tout en RTL.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  // Synchronisation depuis le profil serveur (Dashboard pose oba_lang puis
  // émet cet événement quand l'utilisateur n'avait pas de choix local).
  useEffect(() => {
    const sync = () => {
      const saved = localStorage.getItem(LANG_KEY);
      if (SUPPORTED.includes(saved)) setLangState(saved);
    };
    window.addEventListener('oba-lang-sync', sync);
    return () => window.removeEventListener('oba-lang-sync', sync);
  }, []);

  const setLang = useCallback((code) => {
    if (!SUPPORTED.includes(code)) return;
    localStorage.setItem(LANG_KEY, code);
    setLangState(code);
    // Persistance profil (si connecté) — silencieuse : la langue locale est
    // déjà appliquée, l'échec réseau ne doit rien bloquer.
    if (localStorage.getItem('authToken')) {
      import('../services/api/apiService')
        .then(({ patchRequest }) => patchRequest('/api/auth/profile', { language: code }))
        .catch(() => {});
    }
  }, []);

  // t(clé) → texte de la langue active. Deux formes de clés supportées :
  //   • clé source à plat  : t("Vue d'ensemble")        (dashboard historique)
  //   • chemin sémantique  : t('landing.hero.ctaPrimary')
  // Repli en cascade : langue active → FALLBACK_LANG (fr) → la clé elle-même.
  // Une langue partiellement traduite affiche donc du français lisible plutôt
  // qu'un chemin technique — aucune page ne peut afficher "landing.hero.sub".
  const t = useCallback(
    (key) => resolve(TRANSLATIONS[lang], key)
          ?? resolve(TRANSLATIONS[FALLBACK_LANG], key)
          ?? key,
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, langs: LANGS }}>
      {children}
    </LanguageContext.Provider>
  );
};

const FALLBACK = { lang: 'fr', setLang: () => {}, t: (s) => s, langs: LANGS };
export const useLang = () => useContext(LanguageContext) || FALLBACK;

export default LanguageContext;
